// Orders carry two independent statuses: our own internal processing state
// (did the platform get this order into ERP?) and ERP's own job status
// (what is ERP doing with it now?). Most storefront admins expect a single
// status that just progresses, so this collapses both into one derived
// "primary status" for glanceable views (list badges, header badge), while
// getJourneySteps renders the same underlying signals as a step-by-step
// timeline for the order detail page. Both raw fields remain visible
// elsewhere on the detail page for anyone who needs the full picture.

// Every way an ERP submission can go wrong, each with its own label so the
// stepper, list and header say *what* happened instead of a generic "Needs
// Attention":
//  - VALIDATION_FAILED: rejected before reaching Pace (unmapped SKU, missing
//    store defaults, loyalty denial...) -- permanent until someone fixes it.
//  - ERP_FAILED: Pace itself returned an error for the submission.
//  - RETRYING: the last attempt failed transiently; the erp-submission-drain
//    worker will try again on its own.
export const ERP_FAILURE_LABELS = {
	VALIDATION_FAILED: 'ERP Rejected',
	ERP_FAILED: 'ERP Failed',
	RETRYING: 'Retrying',
};
const NEEDS_ATTENTION_STATES = Object.keys(ERP_FAILURE_LABELS);

// Label for the "Sent to ERP" step while it isn't complete yet.
const pendingErpStepLabel = (state) => {
	if (ERP_FAILURE_LABELS[state]) return ERP_FAILURE_LABELS[state];
	if (state === 'ERP_SUBMITTING') return 'Submitting';
	return 'Queued';
};

// "Closed" is this shop's actual terminal status in practice (confirmed live
// 2026-08-23: 381/400 sampled jobs were Closed vs. 1 explicitly Shipped), so
// it's treated as equivalent to Shipped for progress purposes, not just relabeled.
//
// Except for custom-fulfillment-only orders (every line partner-fulfilled, e.g.
// LAB): pi_ecommerce_api closes their Pace job at acceptance, so Closed there
// means "handed to the partner", not shipped -- only a real fulfillment
// record (`shipped`) counts.
const isShippedFrom = (paceJobStatus, shipped, customFulfillmentOnly) => {
	if (customFulfillmentOnly) return Boolean(shipped);
	const normalized = String(paceJobStatus || '').toLowerCase();
	return Boolean(shipped) || normalized === 'shipped' || normalized === 'closed';
};

// "In Process" step label for a custom-fulfillment-only order -- Phoenix isn't
// producing it, the partner has it.
const PARTNER_STEP_LABEL = 'Sent to partner';

// Back Order ('b') and Hold ('W', confirmed live) are sub-statuses of "In
// Process", not their own journey step -- they describe *why* a job is
// sitting there, not a different point in the pipeline. Matches both the raw
// single-char code (what the admin simulator sends) and Pace's own
// descriptive text (what a real sync poll's admin_status_description
// carries), same dual-format pattern as isShippedFrom above.
const isBackOrderFrom = (paceJobStatus) => {
	const normalized = String(paceJobStatus || '').toLowerCase();
	return normalized === 'b' || normalized.includes('back order');
};
const isHoldFrom = (paceJobStatus) => {
	const normalized = String(paceJobStatus || '').toLowerCase();
	return normalized === 'w' || normalized.includes('hold');
};

// Test-mode orders reach ERP_ACCEPTED without ever getting an erp_order_number
// (see pi_ecommerce_api's simulateErpAcceptance), so "reached ERP" is true when
// either the job number is present or the processing state itself says accepted.
const hasReachedErp = (state, erpOrderNumber) =>
	Boolean(erpOrderNumber) || state === 'ERP_ACCEPTED';

/**
 * order: { state, erpOrderNumber, paceJobStatus, shipped?, customFulfillmentOnly? }
 * Returns { text, bucket } for StatusBadge.
 */
export function getPrimaryStatus({ state, erpOrderNumber, paceJobStatus, shipped, customFulfillmentOnly }) {
	const cancelled = state === 'CANCELLED';
	const reachedErp = hasReachedErp(state, erpOrderNumber);
	const shippedFlag = isShippedFrom(paceJobStatus, shipped, customFulfillmentOnly);

	if (cancelled) return { text: 'Cancelled', bucket: 'cancelled' };
	// Gated by stores.require_order_approval -- sits before ERP submission,
	// waiting on a staff decision (see orderApprovals.service.ts). Resolves
	// to READY_FOR_ERP (approved) or CANCELLED (rejected), so this state is
	// itself always transient.
	if (state === 'PENDING_APPROVAL') return { text: 'Pending Approval', bucket: 'approval' };
	// Gated by stores.require_order_review -- waits for a store admin to
	// release it to READY_FOR_ERP (see orderReview.service.ts).
	if (state === 'PENDING_REVIEW') return { text: 'Pending Review', bucket: 'review' };
	if (ERP_FAILURE_LABELS[state]) return { text: ERP_FAILURE_LABELS[state], bucket: 'failed' };
	if (shippedFlag) return { text: 'Shipped', bucket: 'shipped' };
	if (reachedErp && customFulfillmentOnly) return { text: PARTNER_STEP_LABEL, bucket: 'partner' };
	if (reachedErp && isBackOrderFrom(paceJobStatus)) return { text: 'Back Order', bucket: 'backorder' };
	if (reachedErp && isHoldFrom(paceJobStatus)) return { text: 'On Hold', bucket: 'hold' };
	// Pace opens every job as "Open" the moment it's created (recorded at
	// acceptance by erpSubmit.service.ts's finalizeAcceptedJob), so a job that
	// reached ERP is always "In Process" -- Back Order/Hold are called out
	// above as their own sub-statuses.
	if (reachedErp) return { text: 'In Process', bucket: 'processing' };
	return { text: 'Processing', bucket: 'pending' };
}

/**
 * Status for the /orders list page. Unlike getPrimaryStatus (used on the
 * detail page header), the list needs a compact column where every row reads
 * as one of the step labels shown on the detail page's journey stepper -- so
 * it's derived from getJourneySteps' current step instead of maintaining a
 * second, wider vocabulary.
 */
export function getListStatus({ state, erpOrderNumber, paceJobStatus, shipped, customFulfillmentOnly }) {
	// Cancelled isn't a step the order was progressing through when it
	// stopped -- collapsing it to whatever step happened to be active (e.g.
	// "Queued") reads as if that step is what's wrong, not that the order
	// was cancelled. Keep it as its own label, same as the detail page badge.
	if (state === 'CANCELLED') return { text: 'Cancelled', bucket: 'cancelled' };
	// Same rationale as getPrimaryStatus above -- called out as its own label
	// rather than reading as whatever step (e.g. "Queued") it happens to be
	// sitting at.
	if (state === 'PENDING_APPROVAL') return { text: 'Approval', bucket: 'approval' };
	// Gated by stores.require_order_review -- waits for a store admin to
	// release it to READY_FOR_ERP (see orderReview.service.ts).
	if (state === 'PENDING_REVIEW') return { text: 'Review', bucket: 'review' };
	const steps = getJourneySteps({ state, erpOrderNumber, paceJobStatus, shipped, customFulfillmentOnly });
	const active = steps.find((step) => step.status !== 'complete');
	if (!active) return { text: 'Shipped', bucket: 'shipped' };
	if (active.status === 'partner') return { text: active.label, bucket: 'partner' };
	if (active.status === 'backorder') return { text: 'Back Order', bucket: 'backorder' };
	if (active.status === 'hold') return { text: 'On Hold', bucket: 'hold' };
	const bucket = active.status === 'error' ? 'failed' : active.key === 'received' ? 'pending' : 'processing';
	return { text: active.label, bucket };
}

/**
 * Four-step journey: Received -> Sent to ERP -> In Process -> Shipped. Each
 * step gets one of: complete | current | error | cancelled | upcoming. Only
 * the first not-yet-complete step can be "current"/"error"/"cancelled" --
 * everything after it stays "upcoming".
 *
 * "Sent to ERP" is a checkpoint, not a place an order waits: a successful
 * submission completes it and makes "In Process" current in the same moment
 * (Pace opens every job as "Open" on creation). Before that, it's the
 * current step labeled "Queued"/"Submitting"; a failed submission turns it
 * red with the specific ERP_FAILURE_LABELS label.
 */
// Shared with the "simulate job status" admin control (orders/detail.js) so
// that dropdown always lists the same steps, in the same order, as the
// journey stepper it's driving.
export const JOURNEY_STEP_DEFINITIONS = [
	{ key: 'received', label: 'Received' },
	{ key: 'submitted', label: 'Sent to ERP' },
	{ key: 'production', label: 'In Process' },
	{ key: 'shipped', label: 'Shipped' },
];

// PENDING_APPROVAL (and PENDING_REVIEW) need no special-casing here: hasReachedErp is false for
// it, so it falls into the same completeUpTo=1 branch as any other order that
// hasn't reached ERP yet (Received complete, Queued current) -- "Pending
// Approval" itself is surfaced via the header/list badges above, not as a
// distinct stepper step.
export function getJourneySteps({ state, erpOrderNumber, paceJobStatus, shipped, customFulfillmentOnly }) {
	const cancelled = state === 'CANCELLED';
	const needsAttention = NEEDS_ATTENTION_STATES.includes(state);
	const reachedErp = hasReachedErp(state, erpOrderNumber);
	const shippedFlag = isShippedFrom(paceJobStatus, shipped, customFulfillmentOnly);

	// Number of steps considered fully complete.
	const completeUpTo = shippedFlag ? 4 : reachedErp ? 2 : 1;

	return JOURNEY_STEP_DEFINITIONS.map((rawStep, i) => {
		const step =
			rawStep.key === 'submitted' && i >= completeUpTo ? { ...rawStep, label: pendingErpStepLabel(state) } : rawStep;
		if (i < completeUpTo) return { ...step, status: 'complete' };
		if (i === completeUpTo) {
			// Back Order/Hold only ever describe the "In Process" step, and only
			// when the order isn't already cancelled/needing attention -- those
			// take priority since they're the more urgent signal.
			// Status stays a distinct 'backorder'/'hold' value (used by
			// getListStatus's badge above) but the step's own label is left
			// alone -- the journey stepper always reads plain "In Process" for
			// these; Back Order/Hold are surfaced elsewhere (header badge, list
			// badge, the raw "ERP Job Status" field), not as a different
			// stepper step or a different-looking "In Process".
			if (step.key === 'production' && !cancelled && !needsAttention) {
				if (customFulfillmentOnly) {
					return { ...step, label: PARTNER_STEP_LABEL, status: 'partner' };
				}
				if (isBackOrderFrom(paceJobStatus)) {
					return { ...step, status: 'backorder' };
				}
				if (isHoldFrom(paceJobStatus)) {
					return { ...step, status: 'hold' };
				}
			}
			return { ...step, status: cancelled ? 'cancelled' : needsAttention ? 'error' : 'current' };
		}
		return { ...step, status: 'upcoming' };
	});
}
